import { LiikkuvaWifiSalesProductOnlineModel } from '../../common/constants/subscriptionConstants.js';
import { FiveGVoiceOfferCode, ModelType, type OnlineModelType, VirtualModelType } from '../../common/enums.js';
import { getEffectiveCharges, getVoiceSMEPricing } from '../../common/utils/addOnUtils.js';
import { getOfferCampaignAssociation } from '../../common/utils/campaignUtils.js';
import { getCommercialProductPrices } from '../../common/utils/commercialProductUtils.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { SalesType } from '../../generated/api/models.js';
import {
  type DependencyRecord,
  getListOfServiceFeesByProductIds,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import { getProductPriceText } from './CampaignProductCard.js';
import { getLegacyOffersContentBase } from './content/CommercialProductContent.js';
import { getMobileM2MCardContents4G, getMobileM2MCardContents5G } from './content/LaitenettiSubscriptionCardContent';
import { getMobileBroadbandSubscriptionCardContents } from './content/MobileBroadbandSubscriptionCardContent';
import { getVoiceSubscriptionCardContents } from './content/VoiceSubscriptionCardContent';
import type { LegacyProductContent } from './content/CommercialProductContent.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type {
  AddOnRulesResponse,
  CampaignAssociation,
  CampaignContext,
  CommercialProduct,
  Offer,
  OnlineModel,
  Subscription,
} from '../../generated/api/models.js';
import type { SubCardWithAnalyticsV4Props } from '../HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils';
import type { SubscriptionCardProps } from '../SubscriptionCard/SubscriptionCard.js';

export const getPricesWithAddons = (
  product: CommercialProduct,
  amount: number,
  addOns: AddedAddon[],
  campaignAssociation?: CampaignAssociation,
  salesType?: SalesType,
  useBundledAddOnPricing?: boolean
): { oneTimePrice: number; recurringPrice: number } => {
  const addOnOneTimePrices = addOns.reduce((sum, addOn) => (addOn.oneTimePrice || 0) + sum, 0);
  const addOnMonthlyPrices = addOns.reduce((sum, addOn) => (addOn.monthlyPrice || 0) + sum, 0);

  let oneTimeCharge: number;
  let monthlyRecurringCharge: number;
  if (useBundledAddOnPricing) {
    const smePricing = getVoiceSMEPricing(product);
    oneTimeCharge = smePricing.oneTimeCharge;
    monthlyRecurringCharge = smePricing.monthlyRecurringCharge;
  } else {
    const cpPrices = getCommercialProductPrices(product, campaignAssociation, salesType);
    oneTimeCharge = cpPrices.oneTimeCharge || 0;
    monthlyRecurringCharge = cpPrices.monthlyRecurringCharge || 0;
  }

  const oneTimePrice = (oneTimeCharge + addOnOneTimePrices) * amount;
  const recurringPrice = (monthlyRecurringCharge + addOnMonthlyPrices) * amount;
  return { oneTimePrice, recurringPrice };
};

export const getFormattedPricesWithAddons = (
  product: CommercialProduct,
  amount: number,
  addOns: AddedAddon[],
  campaignAssociation?: CampaignAssociation,
  salesType?: SalesType,
  useBundledAddOnPricing?: boolean
): { oneTimePrice: string; recurringPrice: string } => {
  // TODO: A bit nasty to "|| ''", would probably need to change "formatSum(...)" so that it always returns a string
  const { oneTimePrice, recurringPrice } = getPricesWithAddons(
    product,
    amount,
    addOns,
    campaignAssociation,
    salesType,
    useBundledAddOnPricing
  );
  return { oneTimePrice: formatSum(oneTimePrice) || '', recurringPrice: formatSum(recurringPrice) || '' };
};

export const getOffer = (offers: Offer[], offerCode?: string): Offer => {
  const offer = offers.find(o => o.offerCode === offerCode);
  if (!offer) {
    throw new Error(`Could not find offer with offerCode: ${offerCode}`);
  }
  return offer;
};

export const getLegacyOfferCodes = (modelType: ModelType, offers: Offer[]): string[] =>
  getLegacyOffersContentBase()
    .filter(productContent => productContent.modelType === modelType)
    .flatMap(productContent => productContent.offerCode)
    .filter(offerCode => offers.some(offer => offer.offerCode === offerCode));

const getLegacyOfferContentWithoutPrice = (offer: Offer, modelType: ModelType): LegacyProductContent => {
  const offerCode = offer.offerCode;
  const legacyContentBase = getLegacyOffersContentBase().find(
    content => content.offerCode === offerCode && content.modelType === modelType
  );

  if (!legacyContentBase) {
    throw new Error(`Could not find 4G content with offer code: ${offerCode}`);
  }

  const commercialProduct = offer.commercialProducts[0];
  const openingFee = getEffectiveCharges(commercialProduct).oneTimeCharge;
  return {
    ...legacyContentBase,
    title: offer.offerName,
    openingFee,
  };
};

export const getLegacyProductContents = (
  salesType: SalesType,
  campaignContexts: CampaignContext[],
  modelType: ModelType,
  offers: Offer[],
  voucher?: string
): LegacyProductContent[] | undefined => {
  if (offers.length === 0) {
    return undefined;
  }
  const legacyOfferCodes = getLegacyOfferCodes(modelType, offers);
  const legacyOffers: Offer[] = legacyOfferCodes.map(offerCode => getOffer(offers, offerCode));
  return legacyOffers.map(offer => {
    const campaignAssociation = getOfferCampaignAssociation(
      offer,
      salesType,
      campaignContexts,
      voucher
    )?.campaignAssociation;
    const commercialProduct = offer.commercialProducts[0];
    const commercialProductForPricing =
      modelType === ModelType.MobileBroadband && (commercialProduct.monthlyRecurringCharge ?? 0) === 0
        ? {
            ...commercialProduct,
            monthlyRecurringCharge: getEffectiveCharges(commercialProduct).monthlyRecurringCharge,
          }
        : commercialProduct;
    const isChangeOffer = salesType === SalesType.CHANGE_OFFER;
    const contentWithoutPrice = getLegacyOfferContentWithoutPrice(offer, modelType);
    const priceTexts = getProductPriceText(commercialProductForPricing, false, campaignAssociation, isChangeOffer);
    return {
      ...contentWithoutPrice,
      campaignAssociation,
      priceTexts,
    };
  });
};

export const isFixedPeriodActive = (isChangeOffer: boolean, subscriptionCardContents: SubscriptionCardProps[]) =>
  isChangeOffer && subscriptionCardContents.length === 0;

export const isMbbModel = (modelType: ModelType) => modelType === ModelType.MobileBroadband;

export const isVoiceModel = (modelType: ModelType) =>
  modelType === ModelType.Voice || modelType === ModelType.VoiceSME || modelType === ModelType.YrityspuheUusi;

export const getSubscriptionCardStaticContent = (
  modelType: OnlineModelType,
  isChangeOffer?: boolean
): SubscriptionCardProps[] => {
  if (modelType === ModelType.YrityspuheUusi) {
    return getVoiceSubscriptionCardContents(false, isChangeOffer);
  } else if (modelType === VirtualModelType.LiikkuvaWifi) {
    const wifiModelCodes = Object.values(LiikkuvaWifiSalesProductOnlineModel) as string[];
    return getMobileBroadbandSubscriptionCardContents(false, isChangeOffer).filter(card =>
      wifiModelCodes.includes(card.publicPage?.onlineModelCode ?? '')
    );
  } else if (modelType === ModelType.MobileBroadband) {
    return getMobileBroadbandSubscriptionCardContents(false, isChangeOffer);
  } else if (modelType === ModelType.MobileM2M) {
    // Always use the horizontal order (5G first, then 4G)
    return [...getMobileM2MCardContents5G(isChangeOffer), ...getMobileM2MCardContents4G(isChangeOffer)];
  }
  return [];
};

const getVisibleOffers = (offers?: Offer[], commercialProductCode?: string) =>
  offers?.filter(off => off.commercialProducts?.[0].commercialProductCode !== commercialProductCode) || [];

export const getLegacyOffersForDropdownDisplay = (
  fixedPeriodActiveFlag: boolean,
  onlineModels: OnlineModel[],
  commercialProductCode?: string,
  offerCodes?: string[]
): Offer[] => {
  if (fixedPeriodActiveFlag || !offerCodes?.length) {
    return [];
  }

  const allOffers = onlineModels.flatMap(model => model.offers ?? []);
  return getVisibleOffers(
    allOffers.filter(offer => offerCodes.includes(offer.offerCode)),
    commercialProductCode
  );
};

const getSubscriptionCardByOffer = (
  offerCode: string,
  subscriptionCards: SubscriptionCardProps[]
): SubscriptionCardProps | undefined => subscriptionCards.find(card => card.selectedOffer === offerCode);

export const getSubscriptionCardByLegacyOffer = (offerCode: string): SubscriptionCardProps | undefined => {
  switch (offerCode) {
    case FiveGVoiceOfferCode.Voice1000Mbps:
      return getSubscriptionCardByOffer(FiveGVoiceOfferCode.Voice5gPlus1000Mbps, getVoiceSubscriptionCardContents());
    case FiveGVoiceOfferCode.Voice600Mbps:
      return getSubscriptionCardByOffer(FiveGVoiceOfferCode.Voice5gPlus600Mbps, getVoiceSubscriptionCardContents());
    case FiveGVoiceOfferCode.Voice300Mbps:
      return getSubscriptionCardByOffer(FiveGVoiceOfferCode.Voice5gPlus300Mbps, getVoiceSubscriptionCardContents());
    default:
      return undefined;
  }
};

export interface OfferProductMapType {
  offerCode: string;
  productId: string;
  offerTransitionPriority?: number;
  serviceFeePrice?: number;
  serviceFeeProductId?: string;
}

const getOfferProductDetail = (offerCodes: string[], offers: Offer[]): OfferProductMapType[] =>
  offers
    .filter(offer => offerCodes.includes(offer.offerCode))
    .map(offer => ({
      offerCode: offer.offerCode,
      productId: offer.commercialProducts[0].commercialProductId || '',
      offerTransitionPriority: offer.commercialProducts[0].offerTransitionPriority,
    }));

export const calculateServiceFee = (
  allTargetOfferGuids: string[],
  consolidatedOffers: Offer[],
  subscription?: Subscription,
  addOnRules?: AddOnRulesResponse
): OfferProductMapType[] => {
  const offerProductMap: OfferProductMapType[] = getOfferProductDetail(allTargetOfferGuids, consolidatedOffers);
  if (!subscription || !addOnRules) {
    return offerProductMap;
  }

  const addOnDependencies = addOnRules?.dependencies[0] as DependencyRecord[];
  const targetCommercialProductIds = offerProductMap.map(offerProduct => offerProduct.productId);

  const offerTransitionPriorityMap: { [s: string]: number } = {};
  offerProductMap.forEach(offerProduct => {
    if (offerProduct.offerTransitionPriority !== undefined) {
      offerTransitionPriorityMap[offerProduct.productId] = offerProduct.offerTransitionPriority;
    }
  });

  const serviceFeeItems = getListOfServiceFeesByProductIds(
    addOnDependencies,
    offerTransitionPriorityMap,
    subscription.commercialProductId || '',
    targetCommercialProductIds
  );

  return offerProductMap.map(offerProduct => ({
    ...offerProduct,
    serviceFeePrice: serviceFeeItems[offerProduct.productId]?.price ?? 0,
    serviceFeeProductId: serviceFeeItems[offerProduct.productId]?.priceItemId,
  }));
};

export const getSelectedServiceFee = (
  offerAndServiceFeeMap: OfferProductMapType[],
  selectedCommercialProductId?: string
) => {
  const selectedOasf = offerAndServiceFeeMap.find(oasf => oasf.productId === selectedCommercialProductId);
  return selectedOasf?.serviceFeePrice !== undefined
    ? {
        price: selectedOasf.serviceFeePrice,
        productId: selectedOasf.serviceFeeProductId || '',
      }
    : undefined;
};

export const prepareOfferAndServiceFeeMap = (
  isChangeOffer: boolean,
  addOnRules?: AddOnRulesResponse,
  onlineModels?: OnlineModel[],
  nettiModel?: OnlineModel,
  subscription?: Subscription
): OfferProductMapType[] => {
  if (!isChangeOffer) {
    return [];
  }
  const consolidatedOffers = onlineModels?.flatMap(om => om.offers).concat(nettiModel?.offers || []) || [];
  const allTargetOfferGuids = consolidatedOffers.map(offer => offer.offerCode);
  return calculateServiceFee(allTargetOfferGuids, consolidatedOffers, subscription, addOnRules);
};

export const getCardContentWithServiceFee = (
  subscriptionCardContents: SubCardWithAnalyticsV4Props[],
  offerAndFeeMap: OfferProductMapType[],
  isChangeOffer: boolean
): SubCardWithAnalyticsV4Props[] =>
  isChangeOffer
    ? subscriptionCardContents.map(cardContent => ({
        ...cardContent,
        serviceFee: offerAndFeeMap.find(oaf => oaf.offerCode === cardContent.selectedOffer)?.serviceFeePrice,
      }))
    : subscriptionCardContents;
